import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import Drawer from "antd/es/drawer";
import cn from "classnames";
import Highcharts from "highcharts";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import {
  hideStackRankDrillDown,
  initialize,
  showStackRankDrillDown,
} from "@app/scene/dashboard/store/actions";
import { isDrillDownVisible } from "@app/scene/dashboard/store/selectors";
import { RootState } from "@app/store/rootReducer";
import Scope from "@app/types/dashboard/Scope";

import consts from "./_consts.module.scss";
import CollapseExpandButton from "./components/CollapseExpandButton";
import DrillDownModal from "./components/DrillDownModal";
import SidebarContent from "./components/SidebarContent";
import styles from "./Sidebar.module.scss";

interface OwnProps {
  activeScope: Scope;
  className?: string;
  view: ViewType;
}

interface Props extends OwnProps {
  drillDownVisible: boolean;
  onHideDrillDown: () => void;
  onInitialize: () => void;
  onShowDrillDown: () => void;
}

const Sidebar: FC<Props> = ({
  className,
  drillDownVisible,
  onHideDrillDown,
  onInitialize,
  onShowDrillDown,
  ...props
}) => {
  useEffect(() => {
    onInitialize();
  }, [onInitialize]);

  const containerRef = useRef<HTMLDivElement>(null);

  const [fixed, setFixed] = useState(true);
  const ignoreHover = useRef(false);
  const [drawerVisible, setDrawerVisibility] = useState(false);
  const handleDrawerClose = useCallback(() => setDrawerVisibility(false), []);

  const handleFixedToggle = useCallback((fixed: boolean) => {
    setFixed(fixed);
    setDrawerVisibility(false);
    if (!fixed) {
      // ignore hover event while collapse animation is still running
      // TODO: consider using onAnimationStart/onAnimationEnd
      ignoreHover.current = true;
      setTimeout(() => {
        ignoreHover.current = false;
      }, parseInt(consts.collapseAnimationTimeout, 10));
    }
    setTimeout(() => {
      // We need to reflow charts when their width changes (since sidebar is shown/hidden),
      // because Highcharts doesn't do that automatically. Doing that on a timeout, and
      // using *2 multiplier because it's too early without it.
      Highcharts.charts.forEach((chart) => chart?.reflow());
    }, parseInt(consts.collapseAnimationTimeout, 10) * 2);
  }, []);

  const handleMouseOver = useCallback(() => {
    if (ignoreHover.current) {
      // ignoring to avoid showing a drawer while sidebar is collapsing
      return;
    }
    setDrawerVisibility(true);
  }, []);

  const Content = (
    <SidebarContent
      fixed={fixed}
      onShowDrillDown={onShowDrillDown}
      onToggle={handleFixedToggle}
      {...props}
    />
  );

  return (
    <ExtendedAnalytics keyPath="Stack Rank">
      <div
        className={cn(styles.container, className, {
          [styles.expandable]: !fixed,
          [styles.fixedSidebar]: fixed,
        })}
        onMouseOver={handleMouseOver}
        ref={containerRef}
      >
        {fixed ? (
          Content
        ) : (
          <>
            <CollapseExpandButton
              className={styles.fixButton}
              fixed={fixed}
              onToggle={handleFixedToggle}
            />
            <Drawer
              className={styles.drawer}
              getContainer={containerRef.current || undefined}
              onClose={handleDrawerClose}
              open={drawerVisible}
              placement="left"
            >
              {Content}
            </Drawer>
          </>
        )}
        {drillDownVisible && <DrillDownModal onHide={onHideDrillDown} />}
      </div>
    </ExtendedAnalytics>
  );
};

const mapStateToProps = (state: RootState) => ({
  drillDownVisible: isDrillDownVisible(state),
});

const mapDispatchToProps = {
  onHideDrillDown: hideStackRankDrillDown,
  onInitialize: initialize.request,
  onShowDrillDown: showStackRankDrillDown,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
